import React, { useEffect, useMemo, useRef } from "react";
import { Button, Drawer, Form, Input, Select } from "antd";

const normalizeRequiredText = (value) => String(value || "").trim();

const normalizeRequiredLowerText = (value) =>
  normalizeRequiredText(value).toLowerCase();

const normalizeOptionalText = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed.length ? trimmed : null;
};

const normalizeOptionalLowerText = (value) => {
  const trimmed = normalizeOptionalText(value);
  return trimmed ? trimmed.toLowerCase() : null;
};

export default function LocationRestrictionDrawer({
  open,
  onClose,
  initialValues,
  submitting,
  onSubmit,
  oliveebApp,
  onOliveebAppChange,
  oliveebAppOptions,
}) {
  const [form] = Form.useForm();

  const restrictionId = initialValues?.id || null;
  const lastSyncKeyRef = useRef(null);

  const defaults = useMemo(
    () => ({
      country: "",
      state: null,
      county: null,
      city: null,
      accessType: "ALLOWED",
      ...initialValues,
      oliveebApp: initialValues?.oliveebApp ?? oliveebApp ?? null,
    }),
    [initialValues, oliveebApp],
  );

  useEffect(() => {
    if (!open) {
      lastSyncKeyRef.current = null;
      return;
    }

    const syncKey = `${open ? 1 : 0}:${restrictionId || "new"}`;
    if (lastSyncKeyRef.current === syncKey) return;
    lastSyncKeyRef.current = syncKey;
    form.setFieldsValue({ ...defaults });
  }, [open, restrictionId, defaults, form]);

  const handleFinish = async (values) => {
    const payload = {
      oliveebApp: values.oliveebApp || null,
      country: normalizeRequiredLowerText(values.country),
      state: normalizeOptionalLowerText(values.state),
      county: normalizeOptionalLowerText(values.county),
      city: normalizeOptionalLowerText(values.city),
      accessType: values.accessType,
    };
    try {
      if (onOliveebAppChange) {
        onOliveebAppChange(payload.oliveebApp);
      }
      await onSubmit(payload);
    } catch {
      // Errors are handled (toast) in the hook; swallow to avoid unhandled rejections.
    }
  };

  return (
    <Drawer
      title={
        initialValues?.id ? "Edit Location Restriction" : "New Location Restriction"
      }
      width={520}
      open={open}
      onClose={onClose}
      zIndex={1040}
      destroyOnClose
      >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={defaults}
      >
        <Form.Item
          label="App Name"
          name="oliveebApp"
          rules={[{ required: true, message: "App Name is required" }]}
        >
          <Select
            showSearch
            allowClear={false}
            placeholder="Select App Name"
            options={oliveebAppOptions}
            optionFilterProp="label"
            filterOption={(input, option) =>
              String(option?.label || "")
                .toLowerCase()
                .includes(String(input || "").toLowerCase())
            }
            onChange={(val) => {
              if (onOliveebAppChange) onOliveebAppChange(val);
            }}
          />
        </Form.Item>

        <Form.Item
          label="Country"
          name="country"
          rules={[{ required: true, message: "Country is required" }]}
        >
          <Input placeholder="e.g. usa" />
        </Form.Item>

        <Form.Item label="State (optional)" name="state">
          <Input placeholder="e.g. wa" />
        </Form.Item>

        <Form.Item label="County (optional)" name="county">
          <Input placeholder="e.g. snohomish" />
        </Form.Item>

        <Form.Item label="City (optional)" name="city">
          <Input placeholder="e.g. bothell" />
        </Form.Item>

        <Form.Item
          label="Access Type"
          name="accessType"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              { value: "ALLOWED", label: "ALLOWED" },
              { value: "UNALLOWED", label: "UNALLOWED" },
            ]}
          />
        </Form.Item>

        <div style={{ textAlign: "right", marginTop: 12 }}>
          <Button onClick={onClose} disabled={submitting} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()} loading={submitting}>
            Save
          </Button>
        </div>
      </Form>
    </Drawer>
  );
}
